import React from 'react';
import { isTruthyAndNotEmpty } from '@stimcar/libs-kernel';
import * as m from '../paraglide/messages.js';
import type { SessionSummaryView } from './api.js';
import { ChevronLeftIcon, ChevronRightIcon } from './icons.js';
import { sessionHistoryCreator, sessionHistoryTitle } from './sessionHistory.js';

export type ConversationRailProps = {
  readonly items: readonly SessionSummaryView[];
  readonly currentSessionId: string | undefined;
  readonly viewerEmail: string;
  readonly showAllConversations: boolean;
  readonly collapsed: boolean;
  readonly targetLabel: string | undefined;
  readonly creating?: boolean;
  readonly onSelect: (urlPath: string) => void;
  readonly onNewConversation: () => void;
  readonly onToggleCollapse: () => void;
  readonly onToggleShowAllConversations: () => void;
};

export type FilterConversationRailItemsInput = {
  readonly items: readonly SessionSummaryView[];
  readonly viewerEmail: string;
  readonly showAllConversations: boolean;
  readonly currentSessionId: string | undefined;
};

export function filterConversationRailItems(
  input: FilterConversationRailItemsInput
): readonly SessionSummaryView[] {
  if (input.showAllConversations) {
    return input.items;
  }
  const viewer = input.viewerEmail.trim().toLowerCase();
  return input.items.filter((item) => {
    if (input.currentSessionId !== undefined && item.id === input.currentSessionId) {
      return true;
    }
    return item.createdByEmail.trim().toLowerCase() === viewer;
  });
}

export function ConversationRail(props: ConversationRailProps): React.ReactElement {
  const {
    items,
    currentSessionId,
    viewerEmail,
    showAllConversations,
    collapsed,
    targetLabel,
    creating: creatingProp,
    onSelect,
    onNewConversation,
    onToggleCollapse,
    onToggleShowAllConversations,
  } = props;
  const creating = creatingProp === true;
  const visibleItems = filterConversationRailItems({
    items,
    viewerEmail,
    showAllConversations,
    currentSessionId,
  });
  const className = collapsed
    ? 'studio-conversation-rail is-collapsed'
    : 'studio-conversation-rail';
  const toggleLabel = collapsed ? m.expandConversationRail() : m.collapseConversationRail();
  const scopeLabel = showAllConversations
    ? m.conversationRailShowMine()
    : m.conversationRailShowAll();

  return (
    <aside className={className} aria-label={m.conversationRail()}>
      <button
        type="button"
        className="studio-conversation-rail-toggle"
        onClick={onToggleCollapse}
        aria-expanded={!collapsed}
        aria-label={toggleLabel}
        title={toggleLabel}
      >
        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </button>
      {collapsed ? undefined : (
        <div className="studio-conversation-rail-body">
          <div className="studio-conversation-rail-header">
            <h2>{m.conversationRail()}</h2>
            {isTruthyAndNotEmpty(targetLabel) ? (
              <p className="studio-conversation-rail-target">{targetLabel}</p>
            ) : undefined}
          </div>
          <button
            type="button"
            className="button button-primary studio-conversation-rail-new"
            disabled={creating}
            onClick={onNewConversation}
          >
            {creating ? m.startingRequest() : m.newConversation()}
          </button>
          <button
            type="button"
            className="button button-secondary studio-conversation-rail-scope"
            aria-pressed={showAllConversations}
            onClick={onToggleShowAllConversations}
          >
            {scopeLabel}
          </button>
          {visibleItems.length === 0 ? (
            <p className="studio-conversation-rail-empty">
              {showAllConversations ? m.conversationHistoryEmpty() : m.conversationRailMineEmpty()}
            </p>
          ) : (
            <ul className="studio-conversation-rail-list">
              {visibleItems.map((item) => {
                const selected = item.id === currentSessionId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={
                        selected
                          ? 'studio-conversation-rail-item is-selected'
                          : 'studio-conversation-rail-item'
                      }
                      onClick={() => {
                        onSelect(item.urlPath);
                      }}
                    >
                      <span className="studio-conversation-rail-title">
                        {sessionHistoryTitle(item)}
                      </span>
                      <span className="studio-conversation-rail-meta">
                        {sessionHistoryCreator(item)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </aside>
  );
}
